<script lang="ts">
export default { name: 'DatePickerInput' };
</script>

<script setup lang="ts">
import { computed, nextTick, ref, shallowRef, useId, watch } from 'vue';
import { CalendarIcon } from '@lucide/vue';
import { CalendarDate, parseDate } from '@internationalized/date';
import {
  PopoverRoot,
  PopoverAnchor,
  PopoverTrigger,
  PopoverPortal,
  PopoverContent,
} from 'reka-ui';
import DatePickerCalendar from '@/components/v2/DatePickerCalendar.vue';
import { parseInput } from '@/components/v2/datePickerInputParse';

const props = withDefaults(
  defineProps<{
    modelValue: string;
    label?: string;
    // outlined: kept for API compatibility with existing callers; currently has no effect.
    outlined?: boolean;
    disabled?: boolean;
    placeholder?: string;
    class?: string;
    minDate?: string;
    maxDate?: string;
  }>(),
  {
    label: '',
    outlined: false,
    disabled: false,
    placeholder: 'MM/DD/YYYY',
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

function safeParse(v: string | undefined): CalendarDate | undefined {
  if (!v || typeof v !== 'string' || !v.trim()) return undefined;
  try {
    const d = parseDate(v.trim());
    // Reconstruct as a concrete CalendarDate: parseDate's return type widens to
    // include CalendarDateTime/ZonedDateTime, but downstream code expects CalendarDate.
    return new CalendarDate(d.year, d.month, d.day);
  } catch {
    return undefined;
  }
}

// shallowRef: CalendarDate is an immutable value-type; deep reactivity is unnecessary
// and breaks the class's private-field nominal typing under vue-tsc.
const dateValue = shallowRef<CalendarDate | undefined>(safeParse(props.modelValue));
const calendarValue = computed<CalendarDate | undefined>(() => dateValue.value);
const minDateParsed = computed<CalendarDate | undefined>(() => safeParse(props.minDate));
const maxDateParsed = computed<CalendarDate | undefined>(() => safeParse(props.maxDate));

watch(
  () => props.modelValue,
  (v) => {
    dateValue.value = safeParse(v);
  },
);

const friendlyText = computed(() => {
  const d = dateValue.value;
  if (!d) return '';
  return new Date(d.year, d.month - 1, d.day).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
});

const editing = ref(false);
const rawText = ref('');
const invalid = ref(false);
const errorMsgId = useId();
const open = ref(false);
const inputRef = ref<HTMLInputElement | null>(null);

function inRange(d: CalendarDate): boolean {
  if (minDateParsed.value && d.compare(minDateParsed.value) < 0) return false;
  if (maxDateParsed.value && d.compare(maxDateParsed.value) > 0) return false;
  return true;
}

function pad2(n: number) {
  return n < 10 ? `0${n}` : String(n);
}
function editableText(d: CalendarDate | undefined): string {
  if (!d) return '';
  return `${pad2(d.month)}/${pad2(d.day)}/${d.year}`;
}

const inputDisplay = computed(() => (editing.value ? rawText.value : friendlyText.value));

async function onFocus(e: FocusEvent) {
  const input = e.target as HTMLInputElement;
  const wasInvalid = invalid.value;
  invalid.value = false;
  if (!wasInvalid) {
    rawText.value = editableText(dateValue.value);
  }
  editing.value = true;
  // Wait for the controlled :value to flip to rawText before selecting,
  // so the user can immediately type a replacement.
  await nextTick();
  input.select();
}

function onCloseAutoFocus(e: Event) {
  // Block Reka's default close-auto-focus (which would bounce focus to the
  // calendar icon trigger). For Esc we focus the input explicitly via
  // onEscapeKeyDown; for date-pick we want the friendly format displayed,
  // which requires NOT focusing the input. preventDefault leaves focus where
  // the browser put it (typically body after the focused day cell unmounts).
  e.preventDefault();
}

function onEscapeKeyDown() {
  // Spec requires Esc inside the calendar return focus to the input.
  inputRef.value?.focus();
}

function commit() {
  // Idempotent: if we've already left edit mode, commit was already processed.
  if (!editing.value) return;
  editing.value = false;
  const trimmed = rawText.value.trim();
  if (!trimmed) {
    invalid.value = false;
    // No-op if already empty: avoids spurious emits when the user blurs an empty field.
    if (dateValue.value !== undefined) {
      dateValue.value = undefined;
      emit('update:modelValue', '');
    }
    return;
  }
  const parsed = parseInput(trimmed);
  if (parsed && inRange(parsed)) {
    invalid.value = false;
    dateValue.value = parsed;
    emit('update:modelValue', parsed.toString());
  } else {
    invalid.value = true;
    editing.value = true;
  }
}

function onInput(e: Event) {
  rawText.value = (e.target as HTMLInputElement).value;
}

function onKeydown(e: KeyboardEvent) {
  // Case-insensitive match: real browser events use 'Enter'/'Escape'/'ArrowDown',
  // but vue-test-utils synthesizes events with the lowercase modifier name (e.g. 'enter', 'esc').
  const key = e.key.toLowerCase();
  if (key === 'enter') {
    e.preventDefault();
    commit();
    // .blur() may cause @blur to invoke commit() again in a real browser;
    // commit() is idempotent (early-returns when editing is already false).
    (e.target as HTMLInputElement).blur();
    return;
  }
  if (key === 'escape' || key === 'esc') {
    if (open.value) {
      e.preventDefault();
      open.value = false;
    }
    return;
  }
  if (e.altKey && key === 'arrowdown') {
    e.preventDefault();
    open.value = true;
  }
}

function onSelect(picked: CalendarDate) {
  invalid.value = false;
  dateValue.value = picked;
  rawText.value = '';
  editing.value = false;
  emit('update:modelValue', picked.toString());
  open.value = false;
}

const wrapperClass = computed(() => {
  // gap-1 (not gap-2) leaves room for the full date text "Jun 14, 2025" in the
  // ~138px-wide inputs that DateRangeSelector renders side-by-side.
  const base = 'input input-bordered flex items-center gap-1 bg-white w-full min-w-0';
  const disabled = props.disabled ? 'input-disabled' : '';
  const error = invalid.value ? 'input-error' : '';
  return [base, disabled, error, props.class].filter(Boolean).join(' ');
});
</script>

<template>
  <div class="form-control w-full">
    <label v-if="label" class="label">
      <span class="label-text text-sm">{{ label }}</span>
    </label>
    <PopoverRoot v-model:open="open">
      <PopoverAnchor as-child>
        <div :class="wrapperClass">
          <input
            ref="inputRef"
            type="text"
            :value="inputDisplay"
            :placeholder="placeholder"
            :disabled="disabled"
            :aria-label="label || 'Date'"
            :aria-invalid="invalid || undefined"
            :aria-describedby="invalid ? errorMsgId : undefined"
            class="grow bg-transparent outline-none placeholder:text-gray-400"
            @focus="onFocus"
            @blur="commit"
            @input="onInput"
            @keydown="onKeydown" />
          <PopoverTrigger as-child>
            <button
              type="button"
              aria-label="Open calendar"
              :disabled="disabled"
              data-test="dpi-calendar-btn"
              class="shrink-0 cursor-pointer text-gray-500 hover:text-gray-700">
              <CalendarIcon :size="18" />
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverPortal>
        <PopoverContent
          side="bottom"
          align="start"
          :side-offset="6"
          :collision-padding="8"
          data-test="dpi-popover"
          class="z-[10000] rounded-xl border border-gray-200 bg-white shadow-lg"
          @close-auto-focus="onCloseAutoFocus"
          @escape-key-down="onEscapeKeyDown">
          <DatePickerCalendar
            :model-value="calendarValue"
            :min-date="minDateParsed"
            :max-date="maxDateParsed"
            @select="onSelect" />
        </PopoverContent>
      </PopoverPortal>
    </PopoverRoot>
    <p
      v-if="invalid"
      :id="errorMsgId"
      data-test="dpi-error"
      class="mt-1 text-xs text-red-600">
      Invalid date
    </p>
  </div>
</template>
